// fonts.ts — load the bundled OFL display face (Chakra Petch, style-bible v2) via the FontFace API
// before the game's canvas text is rasterized, so headings/scores/HUD/buttons render in the brand
// face instead of system-ui. Fredoka is kept as a secondary fallback (FONT.display lists both).
// Vite fingerprints the ?url imports (base './'-safe). Resolves on load OR a short timeout so the
// game (and the headless capture harness) never hangs waiting on a font.
//
// Each Chakra Petch weight ships in two subsets (latin + latin-ext); we register each with its
// unicode-range so glyphs outside the subset fall back through the FONT.display stack instead of
// rendering .notdef tofu (a JS FontFace with no unicodeRange would otherwise claim the full range).
import chakra400 from '../../assets/fonts/chakra-petch-400.woff2?url';
import chakra400ext from '../../assets/fonts/chakra-petch-400-ext.woff2?url';
import chakra500 from '../../assets/fonts/chakra-petch-500.woff2?url';
import chakra500ext from '../../assets/fonts/chakra-petch-500-ext.woff2?url';
import chakra600 from '../../assets/fonts/chakra-petch-600.woff2?url';
import chakra600ext from '../../assets/fonts/chakra-petch-600-ext.woff2?url';
import chakra700 from '../../assets/fonts/chakra-petch-700.woff2?url';
import chakra700ext from '../../assets/fonts/chakra-petch-700-ext.woff2?url';
import fredoka500 from '../../assets/fonts/fredoka-500.woff2?url';
import fredoka600 from '../../assets/fonts/fredoka-600.woff2?url';
import fredoka700 from '../../assets/fonts/fredoka-700.woff2?url';

const LATIN =
  'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD';
const LATIN_EXT =
  'U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF';

// [family, weight, url, unicodeRange?]
type FaceDef = [string, string, string, string?];

const FACES: FaceDef[] = [
  ['Chakra Petch', '400', chakra400, LATIN],
  ['Chakra Petch', '400', chakra400ext, LATIN_EXT],
  ['Chakra Petch', '500', chakra500, LATIN],
  ['Chakra Petch', '500', chakra500ext, LATIN_EXT],
  ['Chakra Petch', '600', chakra600, LATIN],
  ['Chakra Petch', '600', chakra600ext, LATIN_EXT],
  ['Chakra Petch', '700', chakra700, LATIN],
  ['Chakra Petch', '700', chakra700ext, LATIN_EXT],
  // Secondary fallback face (used only if Chakra Petch fails to load).
  ['Fredoka', '500', fredoka500],
  ['Fredoka', '600', fredoka600],
  ['Fredoka', '700', fredoka700],
];

let done = false;

export async function loadFonts(timeoutMs = 1400): Promise<void> {
  if (done || typeof document === 'undefined' || !('fonts' in document)) return;
  const loads = FACES.map(([family, weight, url, unicodeRange]) => {
    try {
      const desc: FontFaceDescriptors = { weight, style: 'normal' };
      if (unicodeRange) desc.unicodeRange = unicodeRange;
      const face = new FontFace(family, `url(${url}) format('woff2')`, desc);
      (document.fonts as FontFaceSet).add(face);
      return face.load().then(
        () => undefined,
        () => undefined,
      );
    } catch {
      return Promise.resolve();
    }
  });
  const all = Promise.all(loads).then(() => {
    done = true;
  });
  const timer = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));
  await Promise.race([all, timer]);
}
